import { SiteLogo } from "@/components/shared/SiteLogo";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { getDealerNotifications, getUnreadNotificationCount, markNotificationRead, markAllNotificationsRead } from "@/lib/api";
import { useChatNotifications } from "@/hooks/useChatNotifications";
import {
  BookOpen,
  Megaphone,
  GraduationCap,
  LogOut,
  Menu,
  X,
  Home,
  ChevronRight,
  FileText,
  ShoppingCart,
  LayoutDashboard,
  MapPin,
  MapPinned,
  Camera,
  Users,
  Bell,
  Check,
  CheckCheck,
  ClipboardList,
  ShieldCheck,
  Shield,
  ExternalLink,
  MessageSquare,
  BarChart3,
} from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";

const REQUIRED_PERMISSIONS = ["knowledge_base", "announcements", "training", "visits"];

const sidebarLinks = [
  { name: "Dashboard", path: "/dealer/dashboard", icon: LayoutDashboard, permission: "dashboard" },
  { name: "My Locations", path: "/dealer/locations", icon: MapPinned, permission: "locations" },
  { name: "Photos, Reviews & Events", path: "/dealer/store-content", icon: Camera, permission: "locations" },
  { name: "Dealer Sales", path: "/dealer/verizon-sales", icon: BarChart3, permission: "dealer_sales" },
  { name: "Knowledge Base", path: "/dealer/knowledge-base", icon: BookOpen, permission: "knowledge_base" },
  { name: "Announcements", path: "/dealer/announcements", icon: Megaphone, permission: "announcements" },
  { name: "Submit New Location", path: "/dealer/submit-location", icon: MapPin, permission: "submit_location" },
  { name: "Training", path: "/dealer/training", icon: GraduationCap, permission: "training" },
  { name: "Documents", path: "/dealer/documents", icon: FileText, permission: "documents" },
  { name: "Marketplace", path: "/dealer/marketplace", icon: ShoppingCart, permission: "marketplace" },
  { name: "My Visits", path: "/dealer/visits", icon: ClipboardList, permission: "visits" },
  { name: "Chat", path: "/dealer/chat", icon: MessageSquare, permission: "chat" },
  { name: "Applicants", path: "/dealer/applicants", icon: ClipboardList, permission: "applicants" },
  { name: "Employees", path: "/dealer/employees", icon: Users, ownerOnly: true },
];

const NOTIF_ICONS = {
  location_status: MapPin,
  location_assigned: MapPinned,
  order_status: ShoppingCart,
  announcement: Megaphone,
  knowledge_base: BookOpen,
  training: GraduationCap,
};

const NOTIF_COLORS = {
  location_status: "text-amber-500 bg-amber-50",
  location_assigned: "text-teal bg-teal/10",
  order_status: "text-blue-500 bg-blue-50",
  announcement: "text-purple-500 bg-purple-50",
  knowledge_base: "text-emerald-500 bg-emerald-50",
  training: "text-indigo-500 bg-indigo-50",
};

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
}

function NotificationDropdown({ onClose }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    getDealerNotifications().then(setNotifications).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  const handleMarkRead = async (notif) => {
    if (!notif.read) {
      await markNotificationRead(notif.id).catch(() => {});
      setNotifications((prev) => prev.map((n) => n.id === notif.id ? { ...n, read: true } : n));
    }
    if (notif.link) { navigate(notif.link); onClose(); }
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead().catch(() => {});
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div ref={ref} className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="notification-dropdown">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
        <h3 className="font-heading font-semibold text-navy text-sm">Notifications</h3>
        {unreadCount > 0 && (
          <button onClick={handleMarkAllRead} className="flex items-center gap-1 text-xs text-teal hover:text-teal/80 font-medium" data-testid="mark-all-read-btn">
            <CheckCheck className="w-3.5 h-3.5" /> Mark all read
          </button>
        )}
      </div>
      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="p-6 text-center text-sm text-slate-400">Loading...</div>
        ) : notifications.length === 0 ? (
          <div className="p-6 text-center">
            <Bell className="w-8 h-8 text-slate-200 mx-auto mb-2" />
            <p className="text-sm text-slate-400">No notifications yet</p>
          </div>
        ) : (
          notifications.map((notif) => {
            const Icon = NOTIF_ICONS[notif.type] || Bell;
            const colorClass = NOTIF_COLORS[notif.type] || "text-slate-500 bg-slate-50";
            return (
              <button
                key={notif.id}
                onClick={() => handleMarkRead(notif)}
                className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors flex items-start gap-3 ${!notif.read ? "bg-teal/5" : ""}`}
                data-testid={`notification-${notif.id}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${colorClass}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-medium truncate ${!notif.read ? "text-navy" : "text-slate-600"}`}>{notif.title}</p>
                    {!notif.read && <span className="w-2 h-2 rounded-full bg-teal flex-shrink-0" />}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notif.message}</p>
                  <p className="text-xs text-slate-400 mt-1">{timeAgo(notif.created_at)}</p>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

export const DealerLayout = ({ children, title, breadcrumb }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user, logout, isImpersonating, returnToAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  useChatNotifications();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;

  const isOwner = !user?.employee_id;
  const isEmployee = !!user?.employee_id;
  const employeePerms = user?.dealer_permissions || [];

  const visibleLinks = user?.role === "applicant"
    ? sidebarLinks.filter((l) => l.path === "/dealer/documents")
    : sidebarLinks.filter((l) => {
        if (l.ownerOnly && !isOwner) return false;
        if (isEmployee && l.permission) {
          return employeePerms.includes(l.permission);
        }
        return true;
      });

  const blocked = isEmployee && sidebarLinks.some((l) => l.path === location.pathname && (l.ownerOnly || (l.permission && !employeePerms.includes(l.permission))));
  useEffect(() => {
    if (blocked) navigate(visibleLinks[0]?.path || "/dealer/knowledge-base", { replace: true });
  }, [blocked, navigate, visibleLinks]);

  const fetchUnread = useCallback(() => {
    if (user?.role === "applicant") return;
    getUnreadNotificationCount().then((data) => setUnreadCount(data.count)).catch(() => {});
  }, [user]);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  const handleNotifClose = useCallback(() => {
    setNotifOpen(false);
    fetchUnread();
  }, [fetchUnread]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dealer-layout">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 glass h-14 flex items-center justify-between px-4">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-md text-slate-600 hover:bg-slate-100"
          data-testid="dealer-mobile-menu-btn"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <span className="font-heading font-bold text-navy">Dealer Portal</span>
        <div className="relative">
          <button onClick={() => setNotifOpen(!notifOpen)} className="p-2 rounded-md text-slate-600 hover:bg-slate-100 relative" data-testid="mobile-notif-bell">
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[18px] h-[18px]">{unreadCount > 9 ? "9+" : unreadCount}</span>
            )}
          </button>
          {notifOpen && <NotificationDropdown onClose={handleNotifClose} />}
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-navy z-40 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="dealer-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="px-4 py-3 border-b border-white/10">
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-white rounded-lg p-2">
                <SiteLogo className="h-14 w-auto" testId="dealer-sidebar-logo" />
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto scrollbar-thin">
            <p className="label-style text-slate-400 mb-1.5 px-1">Dealer Portal</p>
            {visibleLinks.map((link) => {
              const Icon = link.icon;
              if (link.external) {
                return (
                  <a
                    key={link.path}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`dealer-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="flex items-center gap-2.5 px-3 py-1.5 rounded-md transition-colors text-slate-300 hover:bg-white/10 hover:text-white"
                  >
                    <Icon className="w-4 h-4" />
                    <span className="font-body text-sm">{link.name}</span>
                    <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                  </a>
                );
              }
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`dealer-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-2.5 px-3 py-1.5 rounded-md transition-colors ${
                    isActive(link.path)
                      ? "bg-teal text-white"
                      : "text-slate-300 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-body text-sm">{link.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="px-3 py-2.5 border-t border-white/10">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-8 h-8 rounded-full bg-teal/20 flex items-center justify-center">
                <span className="text-teal font-semibold text-xs">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-white font-medium text-sm truncate">{user?.first_name} {user?.last_name}</p>
                <p className="text-slate-400 text-xs truncate">{user?.email}</p>
              </div>
            </div>
            {isImpersonating && (
              <Button size="sm" onClick={returnToAdmin} data-testid="return-to-admin-btn"
                className="w-full mb-2 h-8 text-xs bg-amber-500 hover:bg-amber-600 text-white">
                <Shield className="w-3.5 h-3.5 mr-1" /> Return to Admin Portal
              </Button>
            )}
            <div className="flex gap-2">
              <Link to="/" className="flex-1">
                <Button variant="outline" size="sm" className="w-full border-white/20 text-white hover:bg-white/10 h-8 text-xs">
                  <Home className="w-3.5 h-3.5 mr-1" />
                  Home
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-white/20 text-white hover:bg-white/10 h-8"
                data-testid="dealer-logout-btn"
              >
                <LogOut className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="lg:pl-64">
        <main className="min-h-screen pt-14 lg:pt-0">
          {/* Header */}
          <div className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div>
                {breadcrumb && (
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                    <Link to="/dealer" className="hover:text-teal">Dashboard</Link>
                    <ChevronRight className="w-4 h-4" />
                    <span className="text-slate-700">{breadcrumb}</span>
                  </div>
                )}
                <h1 className="font-heading font-bold text-xl lg:text-2xl text-navy" data-testid="page-title">
                  {title}
                </h1>
              </div>
              {/* Desktop notification bell */}
              <div className="hidden lg:block relative">
                <button
                  onClick={() => setNotifOpen(!notifOpen)}
                  className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-navy transition-colors relative"
                  data-testid="desktop-notif-bell"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[18px] h-[18px]">{unreadCount > 9 ? "9+" : unreadCount}</span>
                  )}
                </button>
                {notifOpen && <NotificationDropdown onClose={handleNotifClose} />}
              </div>
            </div>
          </div>

          {/* Page content */}
          <div className="p-4 lg:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
